import React from 'react';
import { Link } from 'react-router-dom';
import { Category } from '../types/woocommerce';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface CategoryMenuProps {
  categories: Category[];
  currentCategoryId?: number;
}

interface CategoryWithChildren extends Category {
  children?: CategoryWithChildren[];
}

export const CategoryMenu: React.FC<CategoryMenuProps> = ({ categories, currentCategoryId }) => {
  // Construire l'arbre de catégories
  const buildCategoryTree = (cats: Category[]): CategoryWithChildren[] => {
    const categoryMap = new Map<number, CategoryWithChildren>();
    const tree: CategoryWithChildren[] = [];

    // Créer une map pour un accès rapide
    cats.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    // Construire l'arbre
    cats.forEach(cat => {
      const category = categoryMap.get(cat.id)!;
      if (cat.parent === 0) {
        tree.push(category);
      } else {
        const parent = categoryMap.get(cat.parent);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(category);
        }
      }
    });

    return tree;
  };

  const categoryTree = buildCategoryTree(categories);

  const renderCategory = (category: CategoryWithChildren, level: number = 0) => {
    const isActive = currentCategoryId === category.id;
    const hasChildren = category.children && category.children.length > 0;

    return (
      <li key={category.id}>
        <Link
          to={`/category/${category.slug}`}
          className={`
            flex items-center justify-between px-4 py-2 text-sm rounded-lg transition-colors
            ${isActive 
              ? 'bg-primary-100 text-primary-700 font-medium' 
              : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'
            }
            ${level > 0 ? `ml-${level * 4}` : ''}
          `}
          style={{ paddingLeft: `${(level * 1) + 1}rem` }}
        >
          <span className="flex items-center gap-2">
            {category.name}
            <span className="text-xs text-gray-500">({category.count})</span>
          </span>
          {hasChildren && (
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          )}
        </Link>
        
        {hasChildren && (
          <ul className="mt-1">
            {category.children!.map(child => renderCategory(child, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav className="space-y-1">
      <h3 className="text-lg font-semibold mb-4">Catégories</h3>
      <ul className="space-y-1">
        <li>
          <Link
            to="/products"
            className={`
              flex items-center justify-between px-4 py-2 text-sm rounded-lg transition-colors
              ${!currentCategoryId 
                ? 'bg-primary-100 text-primary-700 font-medium' 
                : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'
              }
            `}
          >
            Tous les produits
          </Link>
        </li>
        {categoryTree.map(category => renderCategory(category))}
      </ul>
    </nav>
  );
};

